import React, { useMemo } from 'react';
import { HexColorPicker } from 'react-colorful';
import * as Popover from '@radix-ui/react-popover';
import { debounce } from 'lodash';

import { useFilters } from 'context/FilterProvider';
import { ColorPickerBadge, DataTable } from 'components';
import { entity_color, useSetEntityColorMutation, useSystem } from 'store';
import { UserRole } from 'models';
import { userIs } from 'utils';

interface ColorPickerCellProps {
	id: number;
	entity_type: entity_color;
	initialColor: string;
	readOnly?: boolean;
}

const ColorPickerCell = React.memo(({ id, initialColor, entity_type, readOnly }: ColorPickerCellProps) => {
	const [color, setColor] = React.useState(initialColor);
	const [updateEntity] = useSetEntityColorMutation();

	// Create a debounced version of updateEntity
	const debouncedUpdate = React.useMemo(() => {
		return debounce((newColor) => {
			updateEntity({ entity_type, entity_id: id, color: newColor, facility_id: null });
		}, 1000);
	}, [entity_type, id, updateEntity]);

	// Cleanup on unmount
	React.useEffect(() => {
		return () => {
			debouncedUpdate.cancel();
		};
	}, [debouncedUpdate]);

	return (
		<ColorPickerBadge
			color={color}
			onChange={(newColor) => {
				if (!readOnly) {
					setColor(newColor);
					debouncedUpdate(newColor);
				}
			}}
			disabled={readOnly}
		/>
	);
});
ColorPickerCell.displayName = 'ColorPickerCell';

export function FilterSettings() {
	const { serviceLines, encounterTypes, rooms, blockNames } = useFilters();

	const { data: currentUser } = useSystem();
	const isAdmin = userIs([UserRole.admin, UserRole.healthsystem_admin], currentUser?.user);

	const serviceLineColumns = [
		{
			header: 'Service Line',
			accessorKey: 'name',
			meta: { headerClass: 'whitespace-nowrap w-96' },
		},
		{
			header: 'Color',
			cell: (info: { row: { original: any } }) => (
				<ColorPickerCell
					readOnly={!isAdmin}
					id={info.row.original.id}
					initialColor={info.row.original.color}
					entity_type='service_line'
				/>
			),
			meta: { headerClass: 'whitespace-nowrap' },
		},
	];

	const encounterTypeColumns = [
		{
			header: 'Encounter Type',
			accessorKey: 'name',
			meta: { headerClass: 'whitespace-nowrap w-96' },
		},
		{
			header: 'Color',
			cell: (info: { row: { original: any } }) => (
				<ColorPickerCell
					readOnly={!isAdmin}
					id={info.row.original.id}
					initialColor={info.row.original.color}
					entity_type='encounter_type'
				/>
			),
			meta: { headerClass: 'whitespace-nowrap' },
		},
	];

	const roomColumns = [
		{
			header: 'Room',
			accessorKey: 'name',
			meta: { headerClass: 'whitespace-nowrap w-96' },
		},
		{
			header: 'Color',
			cell: (info: { row: { original: any } }) => (
				<ColorPickerCell
					readOnly={!isAdmin}
					id={info.row.original.id}
					initialColor={info.row.original.color}
					entity_type='room_name'
				/>
			),
			meta: { headerClass: 'whitespace-nowrap' },
		},
	];

	const blockNameColumns = [
		{
			header: 'Block Name',
			accessorKey: 'name',
			meta: { headerClass: 'whitespace-nowrap w-96' },
		},
		{
			header: 'Color',
			cell: (info: { row: { original: any } }) => (
				<ColorPickerCell
					readOnly={!isAdmin}
					id={info.row.original.id}
					initialColor={info.row.original.color}
					entity_type='block_name'
				/>
			),
			meta: { headerClass: 'whitespace-nowrap' },
		},
	];

	return (
		<div className='space-y-8'>
			<div className='text-p3 text-gray-400 my-5'>
				This page allows you to customize filter color for different categories, enabling personalized visualization and
				organization of data across various pages.
			</div>

			<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
				<div className='md:pr-6 md:border-r border-b'>
					<DataTable
						title='Service Lines'
						columns={serviceLineColumns}
						data={serviceLines.all}
						minimalStyle
						disableRowCounter
						keepPageIndexOnLeave='ServiceLines'
					/>
				</div>

				<div className='md:pl-6 border-b'>
					<DataTable
						title='Encounter Types'
						columns={encounterTypeColumns}
						data={encounterTypes.all}
						minimalStyle
						disableRowCounter
						keepPageIndexOnLeave='EncounterTypes'
					/>
				</div>
			</div>

			<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
				<div className='md:pr-6 md:border-r'>
					<DataTable
						title='Rooms'
						columns={roomColumns}
						data={rooms.all}
						minimalStyle
						disableRowCounter
						keepPageIndexOnLeave='Rooms'
					/>
				</div>

				<div className='md:pl-6'>
					<DataTable
						title='Block Names'
						columns={blockNameColumns}
						data={blockNames.all}
						minimalStyle
						disableRowCounter
						keepPageIndexOnLeave='BlockNames'
					/>
				</div>
			</div>
		</div>
	);
}
